import { TypedDocumentNode, gql } from '@apollo/client';
import { useMemo } from 'react';
import { Outlet, generatePath } from 'react-router-dom';

import { useTypedParams } from '@sorare/routing';

import { ContentWithStickyHeader } from '@sorare/core/src/atoms/layout/ContentWithStickyHeader';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { PageHeader } from '@sorare/core/src/components/navigation/PageHeader';
import {
  PlayerPageTabs,
  Tabs,
  Props as TabsProps,
} from '@sorare/core/src/components/seo/Player/Tabs';
import { RotoWireRedirect } from '@sorare/core/src/components/seo/redirects/RotoWireRedirect';
import {
  MLB_PLAYERS_SLUG,
  MLB_PLAYERS_SLUG_CARDS,
  MLB_PLAYERS_SLUG_GAMES,
  MLB_PLAYERS_SLUG_TRANSACTIONS,
} from '@sorare/core/src/constants/__generated__/routes';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useTitleAndDescription } from '@sorare/core/src/hooks/useTitleAndDescription';
import { metadatas } from '@sorare/core/src/lib/seo/common';

import {
  BaseballSeoPlayerQuery,
  BaseballSeoPlayerQueryVariables,
} from './__generated__/layout.graphql';
import { LayoutParams } from './__generated__/routeParams';

const BASEBALL_SEO_PLAYER_QUERY = gql`
  query BaseballSeoPlayerQuery($slug: String!) {
    anyPlayer(slug: $slug) {
      slug
      displayName
      avatarPictureUrl
    }
  }
` as TypedDocumentNode<BaseballSeoPlayerQuery, BaseballSeoPlayerQueryVariables>;

const BaseballPlayerLayout = () => {
  const { slug } = useTypedParams<LayoutParams>();
  const { data, loading } = useQuery(BASEBALL_SEO_PLAYER_QUERY, {
    variables: {
      slug,
    },
  });

  const baseballPlayer = data?.anyPlayer;

  useTitleAndDescription(
    metadatas.player.title,
    metadatas.player.description,
    {
      display_name: baseballPlayer?.displayName,
    },
    {
      img: baseballPlayer?.avatarPictureUrl,
    }
  );

  const tabs: TabsProps['tabs'] = useMemo(() => {
    if (!slug)
      return {} as Record<
        Exclude<PlayerPageTabs, PlayerPageTabs.SUPPLY>,
        { to: string; onClick?: () => void }
      >;

    return {
      [PlayerPageTabs.OVERVIEW]: {
        to: generatePath(MLB_PLAYERS_SLUG, {
          slug,
        }),
      },
      [PlayerPageTabs.GAMES]: {
        to: generatePath(MLB_PLAYERS_SLUG_GAMES, {
          slug,
        }),
      },
      [PlayerPageTabs.TRANSACTIONS]: {
        to: generatePath(MLB_PLAYERS_SLUG_TRANSACTIONS, {
          slug,
        }),
      },
      [PlayerPageTabs.CARDS]: {
        to: generatePath(MLB_PLAYERS_SLUG_CARDS, {
          slug,
        }),
      },
    };
  }, [slug]);

  if (loading) return <LoadingIndicator fullHeight />;
  if (!slug || !baseballPlayer) return null;

  return (
    <>
      <PageHeader text={baseballPlayer.displayName} />
      <RotoWireRedirect />
      <ContentWithStickyHeader header={<Tabs tabs={tabs} />}>
        <Outlet />
      </ContentWithStickyHeader>
    </>
  );
};

export default BaseballPlayerLayout;
